import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { Modal } from 'react-bootstrap';
import { Button, Typography } from '@packages/ui/shared';
import { colors } from '@packages/core/styles';
import { AssociateVehiclesPicker, VehicleOption } from './associate-vehicles-picker';

type Props = {
    show: boolean;
    options: VehicleOption[];
    isLoading: boolean;
    selected: string[];
    allVehicles: boolean;
    onHide: () => void;
    onSave: (values: { vehicleIds: string[]; allVehicles: boolean }) => void;
};

const useStyles = createUseStyles({
    body: {
        maxHeight: '70vh',
        overflowY: 'auto',
    },
    intro: {
        marginBottom: 16,
        color: colors.textDarkSecondary,
    },
    footer: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
});

export const AssociateVehiclesModal: FC<Props> = ({
    show,
    options,
    isLoading,
    selected,
    allVehicles,
    onHide,
    onSave,
}) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const [draftSelected, setDraftSelected] = useState<string[]>(selected);
    const [draftAllVehicles, setDraftAllVehicles] = useState<boolean>(allVehicles);
    const [wasShown, setWasShown] = useState(show);
    const savedKey = `${allVehicles}:${selected.join(',')}`;
    const [shownSavedKey, setShownSavedKey] = useState(savedKey);
    const [isTouched, setIsTouched] = useState(false);

    const resetDraft = () => {
        setDraftSelected(selected);
        setDraftAllVehicles(allVehicles);
        setShownSavedKey(savedKey);
    };

    if (show !== wasShown) {
        setWasShown(show);
        if (show) {
            resetDraft();
            setIsTouched(false);
        }
    } else if (show && savedKey !== shownSavedKey && !isTouched) {
        resetDraft();
    }

    const changeSelected = (next: string[]) => {
        setIsTouched(true);
        setDraftSelected(next);
    };

    const changeAllVehicles = (next: boolean) => {
        setIsTouched(true);
        setDraftAllVehicles(next);
    };

    const clearsSavedSelection =
        (allVehicles || selected.length > 0) && !draftAllVehicles && draftSelected.length === 0;

    return (
        <Modal show={show} onHide={onHide} size="lg" centered backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>{t('stisPublications:documentInfo.headers.section2', 'Associate Vehicles')}</Modal.Title>
            </Modal.Header>

            <Modal.Body className={classes.body}>
                <Typography variant="caption" style={{ color: colors.textDarkSecondary }}>
                    {t(
                        'stisPublications:associateVehicles.intro',
                        'Pick the vehicles this publication applies to, or tick All vehicles.'
                    )}
                </Typography>

                <div style={{ marginTop: 16 }}>
                    <AssociateVehiclesPicker
                        options={options}
                        selected={draftSelected}
                        allVehicles={draftAllVehicles}
                        clearsSavedSelection={clearsSavedSelection}
                        isLoading={isLoading}
                        onChange={changeSelected}
                        onAllVehiclesChange={changeAllVehicles}
                    />
                </div>
            </Modal.Body>

            <Modal.Footer>
                <div className={classes.footer}>
                    <Button variant="ghost-blue" onPress={onHide}>
                        {t('stisPublications:associateVehicles.cancel', 'Cancel')}
                    </Button>
                    <Button
                        variant="primary"
                        onPress={() =>
                            onSave({
                                vehicleIds: draftAllVehicles ? [] : draftSelected,
                                allVehicles: draftAllVehicles,
                            })
                        }
                    >
                        {t('stisPublications:associateVehicles.associate', 'Associate')}
                    </Button>
                </div>
            </Modal.Footer>
        </Modal>
    );
};
